import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useAuthStore } from "@/stores/auth-store";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { takeReturnTo } from "@/lib/auth/return-to";

/**
 * Auth callback page — handles the redirect from Microsoft Entra ID.
 *
 * Reads the authorization code from the URL, retrieves the PKCE code verifier
 * from sessionStorage, exchanges them for tokens via the backend, and
 * returns to the page the user first asked for (else the Toolbox home inside
 * SpecTree Toolbox, or the dashboard) on success.
 */
export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { ssoLogin } = useAuthStore();

  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const clearSsoStorage = () => {
      sessionStorage.removeItem("sso_code_verifier");
      sessionStorage.removeItem("sso_state");
    };

    const code = searchParams.get("code");
    if (!code) {
      clearSsoStorage();
      setError("No authorization code received from Microsoft. Please try signing in again.");
      return;
    }

    const codeVerifier = sessionStorage.getItem("sso_code_verifier");
    if (!codeVerifier) {
      clearSsoStorage();
      setError("Authentication session expired. Please try signing in again.");
      return;
    }

    // Validate OAuth state parameter (CSRF protection) before exchanging the code
    const returnedState = searchParams.get("state");
    const storedState = sessionStorage.getItem("sso_state");
    if (!returnedState || !storedState || returnedState !== storedState) {
      clearSsoStorage();
      setError("Authentication session is invalid. Please try signing in again.");
      return;
    }

    let cancelled = false;

    const exchange = async () => {
      try {
        await ssoLogin(code, codeVerifier);

        clearSsoStorage();

        if (cancelled) return;

        void navigate(takeReturnTo(), { replace: true });
      } catch (err) {
        if (cancelled) return;

        clearSsoStorage();

        const message =
          err instanceof Error ? err.message : "Authentication failed";
        setError(message);
      }
    };

    void exchange();

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, ssoLogin]);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b] px-4">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="flex justify-center">
            <div className="rounded-full bg-red-500/10 p-3">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
          </div>

          <div className="space-y-2">
            <h1 className="text-xl font-semibold text-white">
              Sign-in failed
            </h1>
            <p className="text-sm text-white/50 leading-relaxed">{error}</p>
          </div>

          <Button asChild variant="outline" className="border-white/[0.12] bg-white/[0.04] text-white hover:bg-white/[0.06]">
            <Link to="/login">Back to login</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0a0b]">
      <div className="text-center space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-white/50 mx-auto" />
        <p className="text-sm text-white/50">Completing sign-in…</p>
      </div>
    </div>
  );
}
